import { Component, HostListener, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators, } from '@angular/forms';
import { CommonModule, NgClass } from '@angular/common';
import { HeaderComponent } from '../../components/header/header.component';
import { ActivatedRoute, Router } from '@angular/router';
import { FindQuestionPipe } from '../../pipes/find-question.pipe';
import { ButtonComponent } from '../../components/button/button.component';
import { GlobalService } from '../../services/global.service';
import { AssessmentService } from '../../services/assessment.service';
import { EvaluationService } from '../../services/evaluation.service';
import { ModalComponent } from '../../components/modal/modal.component';
import { CheckboxComponent } from '../../components/checkbox/checkbox.component';
import { TextAreaComponent } from '../../components/text-area/text-area.component';
import { ValidationService } from '../../services/validation.service';
import { ExceptionService } from '../../services/exception.service';
import { TextInputComponent } from '../../components/text-input/text-input.component';
import { HttpService } from '../../services/http.service';
import { UserService } from '../../services/user.service';
import { TooltipDirective } from '../../directives/tooltip.directive';

@Component({
  selector: 'app-evaluation-page',
  imports: [FormsModule, CommonModule, HeaderComponent, NgClass, FindQuestionPipe, ButtonComponent, ModalComponent, CheckboxComponent, ReactiveFormsModule, TextAreaComponent, TextInputComponent, TooltipDirective,],
  templateUrl: './evaluation-page.component.html',
  styleUrl: './evaluation-page.component.css',
})
export class EvaluationPageComponent {
  @ViewChild('raise_an_exception') raise_an_exception:
    | ModalComponent
    | undefined;

  page_name: any = 'Evaluation';
  evaluation_data: any;
  form: FormGroup;
  params: any = {};

  // @HostListener('window:beforeunload', ['$event'])
  // on_before_unload(event: BeforeUnloadEvent): void {
  //   this.draft()
  //   event.preventDefault();
  //   (event as any).returnValue = '';
  // }

  marks_data: any[] = [];
  images = [];
  all_levels_data: any[] = [];
  script_images: any[] = []; // Store full ScriptImages data with ids
  script_page_blur: number[] = [];    // per-page blur % for script pages
  supplement_page_blur: number[] = []; // per-page blur % for supplement pages
  supplement_start_index: number = 0;
  total_marks: number = 0;
  max_total_marks: number = 0;
  image_max_height: number = window.innerHeight - 80;

  // Timer for minimum evaluation time
  evaluation_start_time: number = 0;
  min_time_in_seconds: number = 0;
  timer_interval: any;
  elapsed_time: number = 0;
  remaining_time: number = 0;
  formatted_remaining_time: string = '00:00:00';

  constructor(public gs: GlobalService, public es: EvaluationService, public http: HttpService, public ar: ActivatedRoute, public fb: FormBuilder, public vs: ValidationService, public exs: ExceptionService, public us: UserService, public router: Router, public ass: AssessmentService) {
    this.form = this.fb.group({
      evaluation_id: [],
      user_id: [this.gs.user_data.id],
      exception: ['', [Validators.required]],
      description: ['', [Validators.required, Validators.minLength(this.vs.min_length_description), Validators.maxLength(this.vs.max_length_description)]],
      meta_data: [''],
    });
  }

  async ngOnInit() {
    this.calculate_totals();
    this.ar.queryParams.subscribe(async (params) => {
      this.params = { ...params };
    });
    // let res = JSON.parse(localStorage.getItem('draft_evaluation') || '[]');
    // this.dropped = res.annotation || [];
    try {
      this.evaluation_data = await this.es.get(this.params.evaluation_id);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message || 'Failed to load evaluation', 'Error', 'error');
      this.go_to_script_list();
      return;
    }
    this.grace_period_active();

    const data = this.evaluation_data?.data;
    this.all_levels_data = data?.all_levels_data || [];

    // Get all script images sorted (includes both script and supplement pages)
    const sortedScriptImages = data?.script?.ScriptImages
      ?.sort((a: any, b: any) => {
        const nameA = a.image_path?.split('/').pop() || '';
        const nameB = b.image_path?.split('/').pop() || '';
        return nameA.localeCompare(nameB);
      }) || [];

    // Store full ScriptImages data with ids
    this.script_images = sortedScriptImages;

    // Extract only image paths for display
    this.images = sortedScriptImages.map((item: any) => item.image_path) || [];

    // Calculate blur configuration for scripts and supplements
    const noOfPagesPerScript = parseInt(data?.script?.no_of_pages_per_script || '0');
    const noOfPagesPerSupplement = parseInt(data?.script?.no_of_pages_per_supplement || '0');
    const hideScriptPagesForFaculty = parseInt(data?.script?.hide_script_pages_for_faculty || '0');
    const hideScriptPercentage = parseInt(data?.script?.hide_script_percentage || '0');
    const hideSupplementPagesForFaculty = parseInt(data?.script?.hide_supplement_pages_for_faculty || '0');
    const hideSupplementPercentage = parseInt(data?.script?.hide_supplement_percentage || '0');

    // Calculate where supplements start in the images array
    // Script pages: 0 to (noOfPagesPerScript - 1)
    // Supplement pages: noOfPagesPerScript to (totalPages - 1)
    this.supplement_start_index = noOfPagesPerScript;

    // Calculate per-page blur for scripts (hide first N script pages continuously)
    this.script_page_blur = this.calculatePerPageBlur(hideScriptPagesForFaculty, hideScriptPercentage);

    // Calculate per-page blur for supplements (per group):
    // e.g. hideSupplementPagesForFaculty=2, hideSupplementPercentage=65 → coverage=1.3 per group
    // Each supplement group (noOfPagesPerSupplement pages): page 0 → 100%, page 1 → 30%, rest → 0%
    const blurPerGroup = this.calculatePerPageBlur(hideSupplementPagesForFaculty, hideSupplementPercentage);
    const totalSupplementPages = Math.max(0, this.images.length - noOfPagesPerScript);
    this.supplement_page_blur = [];
    if (noOfPagesPerSupplement > 0 && hideSupplementPagesForFaculty > 0 && hideSupplementPercentage > 0) {
      for (let i = 0; i < totalSupplementPages; i++) {
        const posInGroup = noOfPagesPerSupplement > 0 ? i % noOfPagesPerSupplement : i;
        this.supplement_page_blur.push(blurPerGroup[posInGroup] ?? 0);
      }
    }

    // Initialize timer for minimum evaluation time

    // if (this.gs.user_role_data.evaluations_and_revaluations_head_evaluation_view && data.script.head_evaluation_status != 'Completed') { // Head Evaluator
    //   minTimePerScript = await data?.script?.time_left == '00:00:00' ? data?.script?.Assessment?.min_time_per_answer_script : data?.script?.time_left || '00:00:00';
    // } else { // Evaluator
    let minTimePerScript = await data?.script?.time_left == null ? data?.script?.Assessment?.min_time_per_answer_script : data?.script?.time_left || '00:00:00';
    // }
    this.min_time_in_seconds = this.convertTimeToSeconds(minTimePerScript);
    // console.log(data);
    // Use evaluation's createdAt as start time instead of current time
    this.evaluation_start_time = Date.now();
    this.startTimer();

    console.log('=== Timer Configuration ===');
    console.log('Min time per script:', minTimePerScript, '(' + this.min_time_in_seconds + ' seconds)');
    console.log('Evaluation started at:', new Date(this.evaluation_start_time).toLocaleTimeString());

    // patch questions (prefer saved questions, else fallback to rubrics_json)
    const questions =
      Array.isArray(data?.questions) && data.questions.length
        ? data.questions
        : data?.script?.Assessment?.rubrics_json || [];

    // Build a lookup from AiScores by question_number (case-insensitive)
    const aiScoresMap: any = {};
    if (Array.isArray(data?.script?.AiScores)) {
      data.script.AiScores.forEach((a: any) => {
        aiScoresMap[String(a.question_number).toLowerCase()] = a;
      });
    }

    this.marks_data = questions.map((q: any) => {
      const aiEntry = aiScoresMap[String(q.question_number).toLowerCase()];
      // Handle given_mark - keep numeric, use nr/rl flags for NR/RL state
      let givenMark: number = 0;
      let isNR = false;
      let isRL = false;

      if (q.given_mark === 'RL' || q.rl === true || q.rl === 'true') {
        isRL = true;
      } else if (q.given_mark === 'NR' || q.nr === true || q.nr === 'true') {
        isNR = true;
      } else if (q.given_mark !== undefined && q.given_mark !== null && q.given_mark !== '') {
        givenMark = Number(q.given_mark);
      }

      const level_marks: string[] = this.all_levels_data.map((lvl: any) => {
        const lq = lvl.questions?.find((lq: any) => String(lq.question_number) === String(q.question_number));
        if (!lq) return '-';
        if (lq.rl === true || lq.rl === 'true') return 'RL';
        if (lq.nr === true || lq.nr === 'true') return 'NR';
        return String(lq.given_mark ?? '-');
      });

      return {
        question_number: q.question_number,
        ai_score: aiEntry ? Number(aiEntry.ai_score) : (Number(q.ai_score) || 0),
        ai_explanation: aiEntry?.explanation || '',
        optionality: q.optionality,
        parent: q.parent,
        marks: q.marks === 'null' || q.marks === null ? null : Number(q.marks),
        given_mark: givenMark,
        nr: isNR || q.nr === true || q.nr === 'true',
        rl: isRL || q.rl === true || q.rl === 'true',
        level_marks,
      };
    });

    // patch annotations (backend sends it as `annotations` array)
    if (Array.isArray(data?.annotations) && data.annotations.length) {
      this.dropped = data.annotations.map((a: any) => {
        const icon = a.type || 'bx bx-check';
        let img = this.icon_images[icon] || null;
        let _svgString = undefined;

        if (icon === 'drawing' && a.svg) {
          try {
            const blob = new Blob([a.svg], { type: 'image/svg+xml' });
            img = URL.createObjectURL(blob);
            _svgString = a.svg;
          } catch (e) {
            console.error('Error creating object URL for drawing', e);
          }
        } else if ((icon === 'ruler' || icon === 'protractor') && a.svg) {
          img = a.svg;
        } else if (a.img) {
          // Fallback if legacy or other types use img field
          img = a.img;
        }

        return {
          icon,
          img: img,
          _svgString: _svgString,
          x: Number(a.x) || 0,
          y: Number(a.y) || 0,
          dropdown1: a.question_number || undefined,
          dropdown2:
            this.marks_data.find((q) => q.question_number === a.question_number)
              ?.given_mark ?? undefined,
          rotation: 0,
          message: a.message || '',
        };
      });
    } else {
      this.dropped = [];
    }
    console.log(this.marks_data, 'Marks Data');

    // Initialize action history from dropped items
    this.action_history = this.dropped.map((item) => ({
      type: 'dropped',
      item,
    }));

    this.calculate_totals();
    this.refresh_dropdown_options();
  }



  calculate_totals() {
    // 1. Reset all ignored and optional labels flags
    this.marks_data.forEach(item => {
      item.is_optional_ignored = false;
      item.show_optional_label = false;
    });

    // 2. Group child questions by parent
    const groups: { [key: string]: any[] } = {};
    const parentMap: { [key: string]: any } = {};
    const rootNodes: any[] = [];

    this.marks_data.forEach(item => {
      const qNum = String(item.question_number);
      parentMap[qNum] = item;
    });

    this.marks_data.forEach(item => {
      const pNum = item.parent ? String(item.parent) : null;
      if (pNum && parentMap[pNum]) {
        if (!groups[pNum]) groups[pNum] = [];
        groups[pNum].push(item);
      } else {
        rootNodes.push(item);
      }
    });

    // Helper: Mark a node and all its descendants as ignored
    const force_ignore_tree = (node: any) => {
      node.is_optional_ignored = true;
      const qNum = String(node.question_number);
      const children = groups[qNum] || [];
      children.forEach(c => force_ignore_tree(c));
    };

    // Helper: Check if a node tree is fully marked
    const is_node_fully_marked = (node: any): boolean => {
      const qNum = String(node.question_number);
      const children = groups[qNum] || [];
      if (children.length === 0) {
        return node.nr === true || node.rl === true || this.dropped.some((d: any) => String(d.dropdown1) === qNum);
      }
      return children.every(c => is_node_fully_marked(c));
    };

    // Helper: Recursively evaluate a node's marks and apply optionality
    const evaluate_node = (node: any): { v: number, nrOrRlCount: number } => {
      const qNum = String(node.question_number);
      const children = groups[qNum] || [];

      if (children.length === 0) {
        if (node.rl === true || node.nr === true) {
          return { v: 0, nrOrRlCount: 1 };
        }
        return { v: Number(node.given_mark || 0), nrOrRlCount: 0 };
      }

      let childResults = children.map(c => ({
        node: c,
        ...evaluate_node(c)
      }));

      const opt = Number(node.optionality || 0);

      if (opt > 0 && opt < children.length) {
        childResults.sort((a, b) => {
          if (a.v !== b.v) return b.v - a.v;
          return a.nrOrRlCount - b.nrOrRlCount;
        });

        let sum = 0;
        let totalNrOrRl = 0;

        for (let i = 0; i < childResults.length; i++) {
          if (i < opt) {
            sum += childResults[i].v;
            totalNrOrRl += childResults[i].nrOrRlCount;
          } else {
            force_ignore_tree(childResults[i].node);
          }
        }

        const allMarked = children.every(c => is_node_fully_marked(c));
        if (allMarked) {
          const set_optional_label = (n: any) => {
            if (n.is_optional_ignored) {
              const kNum = String(n.question_number);
              const kids = groups[kNum] || [];
              if (kids.length === 0) {
                n.show_optional_label = true;
              } else {
                kids.forEach(k => set_optional_label(k));
              }
            }
          };
          children.forEach(c => set_optional_label(c));
        }

        return { v: sum, nrOrRlCount: totalNrOrRl };
      } else {
        let sum = 0;
        let totalNrOrRl = 0;
        for (let i = 0; i < childResults.length; i++) {
          sum += childResults[i].v;
          totalNrOrRl += childResults[i].nrOrRlCount;
        }
        return { v: sum, nrOrRlCount: totalNrOrRl };
      }
    };

    // 3. Process root nodes to trigger full evaluation
    rootNodes.forEach(rn => evaluate_node(rn));

    // 4. Calculate final totals by summing leaf nodes that are NOT ignored
    this.total_marks = this.marks_data.reduce(
      (sum, item) => {
        const qNum = String(item.question_number);
        const children = groups[qNum] || [];
        // Only sum LEAF nodes
        if (children.length > 0) return sum;

        // Skip RL, NR, and ignored marks
        if (item.rl === true || item.nr === true || item.is_optional_ignored) {
          return sum;
        }
        return sum + Number(item.given_mark || 0);
      },
      0,
    );

    this.max_total_marks = this.marks_data.reduce(
      (sum, item) => {
        if (item.parent == null) return sum;
        return sum + Number(item.marks);
      },
      0,
    );
  }

  refresh_dropdown_options() {
    // Get list of currently selected questions across all dropped items
    const selected_questions =
      this.dropped?.filter((d) => d.dropdown1).map((d) => d.dropdown1) || [];

    this.dropped?.forEach((item) => {
      // 1. Calculate available questions for this item
      // A question is available if:
      // - It is not selected by ANYONE (it's free)
      // - OR it is selected by THIS item (keep it in the list so we can see what we picked)
      const available_qs = this.marks_data.filter(
        (q) =>
          q.parent != null &&
          q.marks > 0 &&
          (!selected_questions.includes(q.question_number) ||
            item.dropdown1 === q.question_number),
      );
      item.available_questions = available_qs;

      // 2. Calculate mark options based on current selection
      const question = this.marks_data.find(
        (q) => q.question_number == item.dropdown1,
      );
      if (question && question.marks) {
        const options = [];
        for (let i = 0; i <= question.marks; i += 0.5) {
          options.push(i);
        }
        item.mark_options = options;
      } else {
        item.mark_options = [];
      }
    });
  }

  on_question_change(item: any, new_question: any) {
    // Reset old question's mark to 0 before switching
    const old_question = this.marks_data.find(q => q.question_number == item.dropdown1);
    if (old_question) {
      old_question.given_mark = 0;
      old_question.nr = false;
      old_question.rl = false;
    }

    // Switch to new question and reset mark selection
    item.dropdown1 = new_question;
    item.dropdown2 = 0;

    this.refresh_dropdown_options();
    this.calculate_totals();
  }

  on_mark_selection_change(item: any) {
    if (
      item.dropdown1 &&
      item.dropdown2 !== undefined &&
      item.dropdown2 !== null
    ) {
      const question = this.marks_data.find(
        (q) => q.question_number == item.dropdown1,
      );
      if (question) {
        this.on_mark_select_change(question, item.dropdown2);
      }
    }
  }

  get_mark_selection(item: any): number | 'NR' | 'RL' {
    if (item?.rl === true) {
      return 'RL';
    }
    if (item?.nr === true) {
      return 'NR';
    }
    return Number(item?.given_mark || 0);
  }

  on_mark_select_change(item: any, value: number | 'NR' | 'RL') {
    item._is_unanswered_error = false;
    if (value === 'NR') {
      item.nr = true;
      item.rl = false;
      item.given_mark = 0;
      item._selected_mark = 'NR';
    } else if (value === 'RL') {
      item.rl = true;
      item.nr = false;
      item.given_mark = 0;
      item._selected_mark = 'RL';
    } else {
      item.given_mark = Number(value);
      item.nr = false;
      item.rl = false;
      item._selected_mark = 0;
    }
    this.calculate_totals();
  }

  toggle_item_dropdown(event: MouseEvent, item: any) {
    if (this.params.view) return;
    item._dropdown_open = !item._dropdown_open;
    item._is_unanswered_error = false;

    if (item._dropdown_open) {
      const button = event.currentTarget as HTMLElement;
      const rect = button.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // Show drop-up if space below is less than 150px
      item._drop_up = spaceBelow < 100;
    }
  }

  is_question_dropped(q_number: string): boolean {
    return this.dropped?.some((d: any) => String(d.dropdown1) === String(q_number)) || false;
  }

  is_marks_panel_open = false;

  toggle_marks_panel() {
    this.is_marks_panel_open = !this.is_marks_panel_open;
  }

  mark_all_as_nr() {
    this.marks_data.forEach((item: any) => {
      if (item.parent != null && item.marks > 0 && !this.is_question_dropped(item.question_number) && !item.nr && !item.rl && !item.given_mark) {
        item.nr = true;
        item.rl = false;
        item.given_mark = 0;
        item._is_unanswered_error = false;
      }
    });
    this.calculate_totals();
  }

  dragging_new = false;
  moving_existing = false;
  current_icon = '';
  current_page = 1;
  active_item: any = null;
  offset_x = 0;
  offset_y = 0;

  dropped: any[] = [];
  drag_floating_icon: string | null = null;
  float_x = 0;
  float_y = 0;

  start_drag_icon(event: PointerEvent, icon: string) {
    this.is_pencil_mode = false
    this.selected_drawing_tool = 'none'
    this.dragging_new = true;
    this.current_icon = icon;

    if (this.icon_images[icon]) {
      this.drag_floating_icon = this.icon_images[icon];
    } else {
      // Boxicons साठी
      this.drag_floating_icon = icon;
    }
    this.float_x = event.clientX + 10;
    this.float_y = event.clientY + 10;
  }
  remove_item(item: any) {
    if (item.dropdown1) {
      const question = this.marks_data.find(
        (q) => q.question_number == item.dropdown1,
      );
      if (question) {
        question.given_mark = 0;
        question.nr = false;
        question.rl = false;
        question.is_touched = false;
      }
    }
    this.dropped = this.dropped.filter((i) => i !== item);
    this.calculate_totals();
    this.refresh_dropdown_options();
    this.update_page_status();
  }
  zoom_level = 1;

  rotate_left(item: any) {
    item.rotation = (item.rotation || 0) - 10;
  }

  rotate_right(item: any) {
    item.rotation = (item.rotation || 0) + 10;
  }

  action_history: { type: 'dropped' | 'drawing'; item: any; }[] = [];
  redo_history: { type: 'dropped' | 'drawing'; item: any; }[] = [];

  undo() {
    if (this.action_history.length > 0) {
      const lastAction = this.action_history.pop();
      if (lastAction && lastAction.type === 'dropped') {
        const index = this.dropped.indexOf(lastAction.item);
        if (index > -1) {
          this.redo_history.push(lastAction);
          this.remove_item(lastAction.item);
        } else {
          // Item might have been removed manually, try next undo
          this.undo();
        }
      } else if (lastAction && lastAction.type === 'drawing') {
        const index = this.drawings.indexOf(lastAction.item);
        if (index > -1) {
          this.redo_history.push(lastAction);
          this.drawings.splice(index, 1);
        }
      }
    }
  }

  redo() {
    if (this.redo_history.length > 0) {
      const action = this.redo_history.pop()!;
      if (action.type === 'dropped') {
        this.dropped.push(action.item);
        // Restore marks if this annotation had a question+mark assigned
        if (action.item.dropdown1 && action.item.dropdown2 != null) {
          const question = this.marks_data.find(
            (q) => q.question_number == action.item.dropdown1,
          );
          if (question) {
            question.given_mark = action.item.dropdown2;
            this.calculate_totals();
          }
        }
        this.refresh_dropdown_options();
      } else if (action.type === 'drawing') {
        this.drawings.push(action.item);
      }
      this.action_history.push(action);
      this.update_page_status();
    }
  }

  restart() {
    this.dropped.forEach((item) => {
      if (item.dropdown1) {
        const question = this.marks_data.find(
          (q) => q.question_number == item.dropdown1,
        );
        if (question) {
          question.given_mark = 0;
        }
      }
    });

    this.dropped = [];
    this.drawings = [];
    this.action_history = [];
    this.redo_history = [];
    this.calculate_totals();
    this.refresh_dropdown_options();
    this.update_page_status();
  }

  zoom_in() {
    this.zoom_level += 0.1;
  }

  zoom_out() {
    if (this.zoom_level > 0.2) {
      this.zoom_level -= 0.1;
    }
  }

  reset_zoom() {
    this.zoom_level = 1;
  }

  start_move_dropped(event: PointerEvent, item: any) {
    if (this.params.view) return;
    event.stopPropagation();
    this.moving_existing = true;
    this.active_item = item;

    // Get the Paper Wrapper element
    const paperWrapper = document.querySelector(
      '.paper-wrapper',
    ) as HTMLElement;
    if (!paperWrapper) return;

    const rect = paperWrapper.getBoundingClientRect();

    // Calculate offset relative to the wrapper
    // IMPORTANT: Divide by zoom_level to get internal wrapper coordinates
    const currentX = (event.clientX - rect.left) / this.zoom_level;
    const currentY = (event.clientY - rect.top) / this.zoom_level;

    this.offset_x = currentX - item.x;
    this.offset_y = currentY - item.y;
  }

  on_move(event: PointerEvent) {
    if (this.dragging_new && this.drag_floating_icon) {
      this.float_x = event.clientX + 11;
      this.float_y = event.clientY + 15;
    }

    if (this.moving_existing && this.active_item) {
      // Get the Paper Wrapper element
      const paperWrapper = document.querySelector(
        '.paper-wrapper',
      ) as HTMLElement;
      if (!paperWrapper) return;

      const rect = paperWrapper.getBoundingClientRect();

      // Calculate new position relative to wrapper
      // IMPORTANT: Divide by zoom_level
      const newX = (event.clientX - rect.left) / this.zoom_level;
      const newY = (event.clientY - rect.top) / this.zoom_level;

      this.active_item.x = newX - this.offset_x;
      this.active_item.y = newY - this.offset_y;
    }
  }
  icon_images: any = {
    ruler:
      'https://cdn.pixabay.com/photo/2015/11/05/05/46/ruler-1023727_1280.png',
    protractor: 'https://www.pngmart.com/files/23/Protractor-PNG-HD.png',
    'bx bx-chat': null,
    'bx bx-check': null,
    'bx bx-x': null,
  };

  on_drop(event: PointerEvent) {
    if (this.dragging_new) {
      // Get the Paper Wrapper element
      const paperWrapper = document.querySelector(
        '.paper-wrapper',
      ) as HTMLElement;
      if (!paperWrapper) return;

      const rect = paperWrapper.getBoundingClientRect();

      // Calculate position relative to wrapper
      // IMPORTANT: Divide by zoom_level
      const x = (event.clientX - rect.left) / this.zoom_level;
      const y = (event.clientY - rect.top) / this.zoom_level;

      this.dropped.push({
        icon: this.current_icon,
        img: this.icon_images[this.current_icon] || null,
        x: x,
        y: y,
        rotation: 0,
        page_index: this.get_page_index_from_y(y),
      });

      this.action_history.push({
        type: 'dropped',
        item: this.dropped[this.dropped.length - 1],
      });
      this.redo_history = [];
      this.refresh_dropdown_options();
    }
    this.drag_floating_icon = null;
    this.dragging_new = false;
    this.moving_existing = false;
    this.active_item = null;
    this.is_drawing_active = false;
    this.update_page_status();
  }

  // Drawing Logic
  is_pencil_mode = false;
  is_drawing_active = false;
  current_path: string[] = [];
  drawings: { path: string; page_index: number; }[] = [];
  selected_drawing_tool: 'freehand' | 'line' | 'circle' | 'square' | 'none' = 'none';
  shape_start_point: { x: number; y: number; } | null = null;
  shape_end_point: { x: number; y: number; } | null = null;
  Math = Math;

  toggle_drawing() {
    this.is_pencil_mode = !this.is_pencil_mode;
    if (!this.is_pencil_mode) {
      this.shape_start_point = null;
      this.shape_end_point = null;
    }
  }

  select_drawing_tool(tool: 'freehand' | 'line' | 'circle' | 'square') {
    if (this.selected_drawing_tool == tool) {
      this.selected_drawing_tool = 'none';
      this.is_pencil_mode = false;
      this.shape_start_point = null;
      this.shape_end_point = null;
      return;
    }
    this.is_pencil_mode = true;
    this.selected_drawing_tool = tool;
    this.shape_start_point = null;
    this.shape_end_point = null;
  }

  calculate_radius(start: { x: number; y: number; }, end: { x: number; y: number; }): number {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  start_drawing(event: PointerEvent) {
    if (!this.is_pencil_mode) return;
    event.stopPropagation();
    this.is_drawing_active = true;

    const paperWrapper = document.querySelector('.paper-wrapper') as HTMLElement;
    if (!paperWrapper) return;
    const rect = paperWrapper.getBoundingClientRect();
    const x = (event.clientX - rect.left) / this.zoom_level;
    const y = (event.clientY - rect.top) / this.zoom_level;

    if (this.selected_drawing_tool === 'freehand') {
      this.current_path = [];
      this.add_point_to_path(event);
    } else {
      // For shapes (line, circle, square), set start point
      this.shape_start_point = { x, y };
      this.shape_end_point = null;
    }
  }

  on_drawing_move(event: PointerEvent) {
    if (!this.is_drawing_active || !this.is_pencil_mode) return;

    if (this.selected_drawing_tool === 'freehand') {
      this.add_point_to_path(event);
    } else {
      // Update end point for shape preview
      const paperWrapper = document.querySelector('.paper-wrapper') as HTMLElement;
      if (!paperWrapper) return;
      const rect = paperWrapper.getBoundingClientRect();
      const x = (event.clientX - rect.left) / this.zoom_level;
      const y = (event.clientY - rect.top) / this.zoom_level;
      this.shape_end_point = { x, y };
    }
  }

  async end_drawing(event: PointerEvent) {
    if (!this.is_drawing_active || !this.is_pencil_mode) return;
    this.is_drawing_active = false;

    // Handle shape tools
    if (this.selected_drawing_tool !== 'freehand' && this.shape_start_point && this.shape_end_point) {
      await this.create_shape_svg();
      this.shape_start_point = null;
      this.shape_end_point = null;
      this.update_page_status();
      return;
    }

    // Handle freehand drawing
    if (this.current_path.length > 0) {
      // 1. Parse path points to find bounding box
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      const points: { x: number; y: number; }[] = [];

      this.current_path.forEach((cmd) => {
        // cmd is like "M 10,20" or "L 30,40"
        const coords = cmd.split(' ')[1];
        if (coords) {
          const [xStr, yStr] = coords.split(',');
          const x = parseFloat(xStr);
          const y = parseFloat(yStr);

          if (!isNaN(x) && !isNaN(y)) {
            points.push({ x, y });
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      });

      // Add padding
      const padding = 5;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      const width = maxX - minX;
      const height = maxY - minY;

      if (width > 0 && height > 0) {
        // 2. Normalize path
        // We'll reconstruct the path string with coordinates relative to (0,0) of the SVG
        let svgPath = '';
        this.current_path.forEach((cmd, index) => {
          const parts = cmd.split(' ');
          const op = parts[0];
          const coords = parts[1].split(',');
          const x = parseFloat(coords[0]) - minX;
          const y = parseFloat(coords[1]) - minY;
          svgPath += `${op} ${x},${y} `;
        });

        // 3. Create SVG string
        const svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}"><path d="${svgPath}" stroke="red" stroke-width="2" fill="none"/></svg>`;

        // 4. Create File object (optional now, but good for uniformity if needed)
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const file = new File([blob], `drawing_${Date.now()}.svg`, {
          type: 'image/svg+xml',
        });
        const objectUrl = URL.createObjectURL(file);

        // 6. Add to dropped items
        this.dropped.push({
          icon: 'drawing',
          drawing_tool: 'freehand',
          img: objectUrl, // Used for display
          _svgString: svgString, // Used for submission
          x: minX,
          y: minY,
          width: width,
          height: height,
          rotation: 0,
          page_index: this.get_page_index_from_y(minY),
        });

        this.action_history.push({
          type: 'dropped',
          item: this.dropped[this.dropped.length - 1],
        });
        this.redo_history = [];
      }
    }
    this.current_path = [];
    this.update_page_status();
  }

  add_point_to_path(event: PointerEvent) {
    const paperWrapper = document.querySelector(
      '.paper-wrapper',
    ) as HTMLElement;
    if (!paperWrapper) return;

    const rect = paperWrapper.getBoundingClientRect();
    const x = (event.clientX - rect.left) / this.zoom_level;
    const y = (event.clientY - rect.top) / this.zoom_level;

    const command = this.current_path.length === 0 ? 'M' : 'L';
    this.current_path.push(`${command} ${x},${y}`);
  }

  get current_drawing_path(): string {
    return this.current_path.join(' ');
  }

  async create_shape_svg() {
    if (!this.shape_start_point || !this.shape_end_point) return;

    const start = this.shape_start_point;
    const end = this.shape_end_point;

    let svgPath = '';
    let minX = 0, minY = 0, width = 0, height = 0;

    if (this.selected_drawing_tool === 'line') {
      // Straight line
      minX = Math.min(start.x, end.x);
      minY = Math.min(start.y, end.y);
      width = Math.abs(end.x - start.x) + 10;
      height = Math.abs(end.y - start.y) + 10;
      const x1 = start.x - minX + 5;
      const y1 = start.y - minY + 5;
      const x2 = end.x - minX + 5;
      const y2 = end.y - minY + 5;
      svgPath = `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="red" stroke-width="2" />`;
    } else if (this.selected_drawing_tool === 'circle') {
      // Circle
      const radius = this.calculate_radius(start, end);
      minX = start.x - radius - 5;
      minY = start.y - radius - 5;
      width = radius * 2 + 10;
      height = radius * 2 + 10;
      const cx = radius + 5;
      const cy = radius + 5;
      svgPath = `<circle cx="${cx}" cy="${cy}" r="${radius}" stroke="red" stroke-width="2" fill="none" />`;
    } else if (this.selected_drawing_tool === 'square') {
      // Rectangle
      minX = Math.min(start.x, end.x);
      minY = Math.min(start.y, end.y);
      width = Math.abs(end.x - start.x);
      height = Math.abs(end.y - start.y);
      svgPath = `<rect x="5" y="5" width="${width}" height="${height}" stroke="red" stroke-width="2" fill="none" />`;
      width += 10;
      height += 10;
    }

    if (width > 0 && height > 0) {
      const svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">${svgPath}</svg>`;
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const file = new File([blob], `shape_${Date.now()}.svg`, { type: 'image/svg+xml' });
      const objectUrl = URL.createObjectURL(file);

      this.dropped.push({
        icon: 'drawing',
        drawing_tool: this.selected_drawing_tool,
        img: objectUrl,
        _svgString: svgString,
        x: minX,
        y: minY,
        width: width,
        height: height,
        rotation: 0,
        page_index: this.get_page_index_from_y(minY),
      });

      this.action_history.push({
        type: 'dropped',
        item: this.dropped[this.dropped.length - 1],
      });
      this.redo_history = [];
    }
  }

  get_page_index_from_y(y: number): number {
    for (let i = 0; i < this.images.length; i++) {
      const imgEl = document.getElementById('page-' + i);
      if (imgEl) {
        const parentDiv = imgEl.parentElement;
        if (parentDiv) {
          const top = parentDiv.offsetTop;
          const bottom = top + imgEl.offsetHeight;
          if (y >= top && y < bottom) return i;
        }
      }
    }
    return this.current_page - 1;
  }

  scroll_to_page(index: number) {
    const element = document.getElementById('page-' + index);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  on_scroll(event: Event) {
    const container = event.target as HTMLElement;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.top + containerRect.height / 2;

    for (let i = 0; i < this.images.length; i++) {
      const element = document.getElementById('page-' + i);
      if (element) {
        const rect = element.getBoundingClientRect();
        // Check if the image covers the center of the container
        if (rect.top <= containerCenter && rect.bottom >= containerCenter) {
          this.current_page = i + 1;
          break;
        }
      }
    }
  }

  focus_question(questionNumber: string) {
    const item = this.dropped.find((d) => d.dropdown1 === questionNumber);
    if (item) {
      const container = document.querySelector(
        '.pdf-content-container',
      ) as HTMLElement;
      if (container) {
        // Calculate scroll position: item.y * zoom_level (since wrapper is zoomed)
        // Subtract some offset (e.g. 100px) to center/show it better
        const scrollAmount = item.y * this.zoom_level - 350;

        container.scrollTo({
          top: scrollAmount,
          behavior: 'smooth',
        });
      }
    }
  }

  async draft() {

    const annotations = this.dropped.map((item) => {
      const question = this.marks_data.find(
        (q) => q.question_number == item.dropdown1,
      );

      // If it's a drawing, send the SVG content
      const isDrawing = item.icon === 'drawing';
      const isRulerOrProtractor =
        item.icon === 'ruler' || item.icon === 'protractor';

      let svgContent = null;
      let imgContent = item.img;

      if (isDrawing) {
        svgContent = item._svgString;
        imgContent = null;
      } else if (isRulerOrProtractor) {
        svgContent = item.img; // Send URL in svg field as requested
        imgContent = null;
      }

      return {
        type: item.icon,
        img: imgContent,
        svg: svgContent,
        x: item.x,
        y: item.y,
        question: question ? [question] : [],
        message: item.message,
      };
    });

    let draft_data = {
      evaluation_id: +this.params.evaluation_id,
      annotations: annotations,
      questions: this.marks_data,
      script_id: this.evaluation_data.data.script.id,
      is_draft: true,
      user_id: +this.gs.user_data.id,
      total_marks: this.total_marks,
      time_left: this.formatted_remaining_time == '' ? '00:00:00' : this.formatted_remaining_time,
    };

    try {
      let res = await this.es.draft(draft_data);
      console.log(res);
    } catch (error: any) {
      this.gs.toastr_shows_function(error.error.message, 'Error', 'error');
    }
    this.go_to_script_list();
    console.log(draft_data);
  }

  navigate_to_assessment() {
    this.router.navigate(['/evaluation/script/list'], { queryParams: { assessment_id: this.evaluation_data.data.script.assessment_id, dashboard: true, evaluation_type: this.params.evaluation_type } });
  }

  get_unanswered_questions(): any[] {
    let groups: { [key: string]: any[] } = {};
    this.marks_data.forEach(item => {
      const pStr = item.parent ? String(item.parent) : null;
      if (pStr) {
        if (!groups[pStr]) groups[pStr] = [];
        groups[pStr].push(item);
      }
    });

    const unanswered = this.marks_data.filter((item) => {
      const qNum = String(item.question_number);

      // Visibility sync: Only validate actionable sidebar items
      if (!(item.parent != null && item.marks > 0)) return false;

      // Skip intermediate parent nodes
      if (groups[qNum] && groups[qNum].length > 0) return false;

      // Skip questions ignored by optionality logic
      // if (item.is_optional_ignored) return false;

      /**
       * A question is considered ANSWERED if:
       * 1. It has been marked by dropping an annotation on the paper.
       * 2. It is explicitly marked as 'NR' (Not Responded).
       * 3. It is explicitly marked as 'RL' (Review Later).
       * 4. It has been given a score greater than 0 from the sidebar.
       */
      const hasMark = this.is_question_dropped(item.question_number) ||
        item.nr === true ||
        item.rl === true ||
        (Number(item.given_mark) > 0);

      return !hasMark;
    });

    return unanswered;
  }

  async submit(redirect: boolean = true) {
    // Check for answered/unanswered
    this.marks_data.forEach(q => q._is_unanswered_error = false);
    const unanswered = this.get_unanswered_questions();

    if (unanswered.length > 0) {
      unanswered.forEach(q => q._is_unanswered_error = true);
      this.gs.toastr_shows_function('Please review all answers. Mark unanswered responses as ‘NR’ (Not Responded) from the sidebar.', 'Error', 'error');
      this.is_marks_panel_open = true
      return;
    }

    // Removed filter for ruler and protractor as requested
    this.grace_period_active()
    const annotations = this.dropped.map((item) => {
      const question = this.marks_data.find(
        (q) => q.question_number == item.dropdown1,
      );

      const isDrawing = item.icon === 'drawing';
      const isRulerOrProtractor =
        item.icon === 'ruler' || item.icon === 'protractor';

      let svgContent = null;
      let imgContent = item.img;

      if (isDrawing) {
        svgContent = item._svgString;
        imgContent = null;
      } else if (isRulerOrProtractor) {
        svgContent = item.img; // Send URL in svg field as requested
        imgContent = null;
      }

      return {
        type: item.icon,
        img: imgContent,
        svg: svgContent,
        x: item.x,
        y: item.y,
        question: question ? [question] : [],
        message: item.message,
      };
    });

    let draft_data: any = {
      evaluation_id: +this.params.evaluation_id,
      annotations: annotations,
      questions: this.marks_data,
      script_id: this.evaluation_data.data.script.id,
      is_draft: true,
      user_id: +this.gs.user_data.id,
      total_marks: this.total_marks,
      time_left: '00:00:00',
      submitted_at: this.grace_period_time || ''
    };
    // if ((this.evaluation_data.data.evaluation_status == 'Completed' && this.evaluation_data.data.head_evaluation_status != 'Completed') || (this.evaluation_data.data.evaluation_status == 'Completed' && this.evaluation_data.data.head_evaluation_status == 'Completed')) {
    //   draft_data.submitted_at.push(this.grace_period_time);
    // }
    console.log(draft_data, 'Submit Data');
    // alert(JSON.stringify(draft_data))
    if (draft_data.questions.length != this.evaluation_data.data?.script?.Assessment?.rubrics_json?.length) {
      this.gs.toastr_shows_function('rubric not matching', 'Error', 'error');
      return;
    } else {
      try {
        let res = await this.es.submit(draft_data);
        // console.log(res);
      } catch (error: any) {
        this.gs.toastr_shows_function(error.error.message, 'Error', 'error');
      }
      if (redirect) this.go_to_script_list();
    }

  }

  page_annotation_status: boolean[] = [];

  async ngAfterViewInit() {
    // Wait for images to render/load to get correct heights
    setTimeout(() => this.update_page_status(), 1000);
    setTimeout(() => this.update_page_status(), 3000); // multiple checks to be safe
  }

  update_page_status() {
    if (!this.images || this.images.length === 0) return;

    const statuses: boolean[] = new Array(this.images.length).fill(false);

    // Get paper wrapper to calculate relative positions
    const paperWrapper = document.querySelector(
      '.paper-wrapper',
    ) as HTMLElement;
    if (!paperWrapper) return;

    // Map pages to their vertical ranges relative to paper-wrapper
    const pageRanges = this.images.map((_, i) => {
      const imgEl = document.getElementById('page-' + i);
      if (imgEl) {
        const parentDiv = imgEl.parentElement;
        if (parentDiv) {
          // Get position relative to paper wrapper
          const relativeTop = parentDiv.offsetTop;
          const height = imgEl.offsetHeight;
          return {
            top: relativeTop,
            bottom: relativeTop + height,
            pageIndex: i,
          };
        }
      }
      return null;
    });

    // Only these annotation types count: Correct, Incorrect, Comment, Seen, Freehand (Mark by Pen), Line
    this.dropped?.forEach((item) => {
      const is_valid_annotation =
        item.icon === 'bx bx-check' ||   // Correct
        item.icon === 'bx bx-x' ||       // Incorrect
        item.icon === 'bx bx-chat' ||    // Comment
        item.icon === 'bx bx-eye' ||     // Seen
        (item.icon === 'drawing' && (
          item.drawing_tool === 'freehand' ||  // Mark by Pen
          item.drawing_tool === 'line' ||      // Line
          item.drawing_tool == null            // loaded from backend (drawing_tool not saved); treat as valid
        ));

      if (!is_valid_annotation) return;

      // Use page_index stored at draw-time (reliable); fall back to coordinate lookup for backend-loaded items
      if (item.page_index != null && item.page_index >= 0 && item.page_index < statuses.length) {
        statuses[item.page_index] = true;
      } else {
        const pageIndex = pageRanges.findIndex(
          (range) => range && item.y >= range.top && item.y < range.bottom,
        );
        if (pageIndex !== -1) {
          statuses[pageIndex] = true;
        }
      }
    });

    this.page_annotation_status = statuses;
  }

  get all_pages_annotated(): boolean {
    if (!this.page_annotation_status || this.page_annotation_status.length === 0) {
      return false; // No pages loaded or status not calculated yet
    }
    return this.page_annotation_status.every(status => status === true);
  }

  get has_rl_marks(): boolean {
    return this.marks_data.some(item => item.rl === true);
  }

  get is_submit_disabled(): boolean {
    const annotations_required = this.evaluation_data?.data?.script?.Assessment?.annotations_required;
    // Disable if minimum time hasn't passed OR (if annotations required and not all pages are annotated) OR if any question has RL mark
    return this.remaining_time > 0 || (annotations_required && !this.all_pages_annotated) || this.has_rl_marks;
  }

  // Calculate per-page blur percentages
  // E.g. pages=2, percentage=75 → total coverage = 2*0.75 = 1.5 pages
  // → Page 0: 100%, Page 1: 50%
  calculatePerPageBlur(pages: number, percentage: number): number[] {
    if (pages <= 0 || percentage <= 0) return [];
    const totalCoverage = pages * (percentage / 100); // e.g. 1.5
    const result: number[] = [];
    let remaining = totalCoverage;
    for (let i = 0; i < pages; i++) {
      if (remaining >= 1) {
        result.push(100);
        remaining -= 1;
      } else if (remaining > 0) {
        result.push(Math.round(remaining * 100));
        remaining = 0;
      } else {
        result.push(0);
      }
    }
    return result;
  }

  convertTimeToSeconds(time: string): number {
    // Convert HH:MM:SS to seconds
    const parts = time.split(':');
    const hours = parseInt(parts[0] || '0');
    const minutes = parseInt(parts[1] || '0');
    const seconds = parseInt(parts[2] || '0');
    return hours * 3600 + minutes * 60 + seconds;
  }

  startTimer(): void {
    this.timer_interval = setInterval(() => {
      this.elapsed_time = Math.floor((Date.now() - this.evaluation_start_time) / 1000);
      this.remaining_time = Math.max(0, this.min_time_in_seconds - this.elapsed_time);
      this.formatted_remaining_time = this.formatTime(this.remaining_time);

      if (this.remaining_time === 0 && this.timer_interval) {
        clearInterval(this.timer_interval);
        console.log('Minimum evaluation time reached. Submit button enabled.');
      }
    }, 1000);
  }

  formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  ngOnDestroy(): void {
    if (this.timer_interval) {
      clearInterval(this.timer_interval);
    }
  }
  async exception() {
    try {
      if (this.form.invalid) {
        this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error',);
        this.form.markAllAsTouched();
        return;
      }

      // Get the current ScriptImage id based on current_page (1-indexed)
      const currentScriptImageIndex = this.current_page - 1;
      const currentScriptImage = this.script_images[currentScriptImageIndex];

      // Build metadata with page_id, script_id, script_image_id, and user_id
      const meta_data = {
        ...this.evaluation_data?.data,
        page_number: this.current_page,
        script_id: this.evaluation_data?.data?.script?.id,
        script_image_id: currentScriptImage?.id,
        image_path: currentScriptImage?.image_path,
        user_id: this.gs.user_data.id,
      };
      this.form.get('meta_data')?.setValue(meta_data);
      this.form.value.evaluation_id = +this.params.evaluation_id;
      console.log(this.params.evaluation_id, this.form.value, 'Exception form value');
      let create_response = await this.exs.add(this.form.value);
      this.form.reset();
      this.raise_an_exception?.close();
      if(this.gs.user_role_data?.evaluations_and_revaluations_head_evaluation_view){
        this.gs.go_back();
      }else{
        await this.submit_and_skip_funcnality()
      }
    } catch (error: any) {
      this.gs.toastr_shows_function(error.error?.message, 'Error', 'error');
    }
  }
  async submit_and_next() {
    // Check for answered/unanswered
    this.marks_data.forEach(q => q._is_unanswered_error = false);
    const unanswered = this.get_unanswered_questions();

    if (unanswered.length > 0) {
      unanswered.forEach(q => q._is_unanswered_error = true);
      this.gs.toastr_shows_function('Please review all answers. Mark unanswered responses as ‘NR’ (Not Responded) from the sidebar.', 'Error', 'error');
      this.is_marks_panel_open = true
      return;
    }
    try {
      await this.submit(false);
      await this.submit_and_skip_funcnality()
    } catch (error: any) {
      this.gs.toastr_shows_function(error.error?.message, 'Error', 'error');
      return
    }
  }

  async submit_and_skip_funcnality() {
    let params: any = {}
    params = {
      assessment_id: this.evaluation_data.data?.script?.assessment_id,
      user_id: this.gs.user_data.id
    }
    const res = await this.es.get_my_evaluation(params);
    let assessment_res = await this.ass.get(this.evaluation_data.data?.script?.Assessment.id, { count: true, evaluation_type: this.params.evaluation_type });
    if (assessment_res.data.in_progress_script == 0 && assessment_res.data.pending_script == 0) {
      this.gs.go_back();
      this.gs.toastr_shows_function('No evaluation found for this assessment', 'Warning', 'warning');
    } else {
      if (res.message == 'No evaluation found for this assessment') {
        const res = await this.es.start_ramdom_evaluation(params);
        if (!res.data || (Array.isArray(res.data) && res.data.length === 0)) {
          this.gs.toastr_shows_function(res.message, 'Warning', 'warning');
          this.router.navigate(['/evaluation/script/list'], { queryParams: { assessment_id: this.evaluation_data.data?.script?.assessment_id, dashboard: true, evaluation_type: this.params.evaluation_type }, });
          return;
        }
        if (res.data.evaluation_id) {
          this.gs.go_back()
          setTimeout(() => {
            window.location.href = '/evalution?evaluation_id=' + res.data.evaluation_id + '&evaluation_type=' + this.params.evaluation_type;
          }, 100);
        } else {
          this.gs.toastr_shows_function(res.message, 'Error', 'error');
        }
      } else if (res.data) {
        window.location.href = '/evalution?evaluation_id=' + res.data.evaluation_id + '&evaluation_type=' + this.params.evaluation_type;
      }
    }
  }

  async get_new_script() {
    let params: any = {}
    params = { assessment_id: this.evaluation_data.data?.script?.assessment_id, user_id: this.gs.user_data.id }
    const res = await this.es.start_ramdom_evaluation(params);
    if (!res.data) {
      this.gs.toastr_shows_function(res.message, 'Error', 'error');
      this.navigate_to_assessment();
      return;
    } else if (res.data.evaluation_id) {
      console.log(res.data.evaluation_id + '&evaluation_type=' + this.params.evaluation_type, 'Response after starting random evaluation');
      window.location.href = '/evalution?evaluation_id=' + res.data.evaluation_id + '&evaluation_type=' + this.params.evaluation_type;
      return;
    }
  }

  grace_period_time: any = null;
  grace_period_active() {
    let date = new Date().toISOString();
    const evalData = this.evaluation_data?.data?.script;
    if (!evalData) return;
    if ((evalData.evaluation_status == 'In Progress' && evalData.head_evaluation_status == 'Pending') || (evalData.evaluation_status == 'Completed' && evalData.head_evaluation_status == 'In Progress')) {
      this.grace_period_time = date;
      console.log(date, 'grace period active function called');
    } else {
      this.grace_period_time = this.evaluation_data?.data?.evaluation?.submitted_at;
      console.log('Evaluator Status', evalData.evaluation_status, 'head Evaluation Status', evalData.head_evaluation_status, "Condition");
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any): void {
    this.image_max_height = window.innerHeight - 80;
  }

  go_to_script_list() {
    if (this.router.url.includes('evaluation/script/list')) return;

    const on_popstate = () => {
      if (window.location.href.includes('evaluation/script/list')) {
        window.removeEventListener('popstate', on_popstate);
        return;
      }
      this.gs.go_back();
    };
    window.addEventListener('popstate', on_popstate);
    this.gs.go_back();
  }
}